// OPTIONS ANALYSIS · Opportunity History (O6) — episodes per pair and entry price mode, with bounded
// samples. Scanner observations only: never fills, never P&L.
import React, { useEffect, useState } from "react";
import { History, X } from "lucide-react";
import optionsApi, { optionsError } from "@/lib/optionsApi";
import { age, istTime, px, usePoll } from "@/components/options/OptionsCommon";

const sel = "bg-[var(--term-input)] border border-[var(--term-border-strong)] rounded-sm px-2 py-1";

function Episode({ id, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!id) return;
    optionsApi.get(`/history/${id}`).then((r) => setD(r.data)).catch((e) => setErr(optionsError(e)));
  }, [id]);
  if (!id) return null;
  const m = d?.at_max;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="w-[760px] max-w-full h-full overflow-auto bg-[var(--term-bg)] border-l border-[var(--term-border-strong)] p-4 space-y-3 text-xs"
        onClick={(e) => e.stopPropagation()} data-testid="options-episode-drawer">
        <div className="flex items-center justify-between">
          <b className="text-sm">{d ? `${d.mode} · ${d.ce_symbol} × ${d.pe_symbol}` : "Episode"}</b>
          <button onClick={onClose} aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        {err && <div className="text-[#FF3B30]">{err}</div>}
        {d && (
          <>
            <div className="term-panel rounded-sm p-3 mono space-y-1">
              <div>{istTime(d.first_seen_at)} → {d.closed_at ? istTime(d.closed_at) : "open"} · {age(d.duration_s)} · {d.n} observations · closed: {d.close_reason || "—"}</div>
              <div>Opportunity/unit first {px(d.opp_first)} · max <b>{px(d.opp_max)}</b> · min {px(d.opp_min)} · mean {px(d.opp_mean)} · last {px(d.opp_last)}</div>
              {m && <div>At max ({istTime(m.at)}): CE {px(m.ce?.ltp)} / offer {px(m.ce?.ask)} · PE {px(m.pe?.ltp)} / offer {px(m.pe?.ask)} · per lot {px(m.opp_lot)} · other mode {px(m.other_mode_opp)} ({m.other_mode_reason})
                {m.capital_required ? ` · capital ₹${Number(m.capital_required).toLocaleString("en-IN")} · ${m.pct_on_capital?.toFixed?.(2) ?? "—"}% on capital` : ""}</div>}
              {d.stale_observations > 0 && <div className="text-[#B45309]">WARNING: {d.stale_observations} of {d.n} observations rested on stale LTP data ({(d.warnings_seen || []).join(", ")})</div>}
            </div>
            <table className="w-full border-collapse mono" data-testid="options-episode-samples">
              <thead className="bg-[var(--term-s3)]"><tr>{["Time", "Spot", "LTP opp", "OFFER opp", "CE LTP", "CE offer", "PE LTP", "PE offer", "Warnings"].map((h) => <th key={h} className="px-1.5 py-1 text-right">{h}</th>)}</tr></thead>
              <tbody>{(d.samples || []).map((s, i) => (
                <tr key={i}><td className="px-1.5 text-right">{istTime(s.at)}</td><td className="px-1.5 text-right">{px(s.spot)}</td>
                  <td className="px-1.5 text-right">{px(s.ltp_opp)}</td><td className="px-1.5 text-right">{px(s.offer_opp)}</td>
                  <td className="px-1.5 text-right">{px(s.ce?.ltp)}</td><td className="px-1.5 text-right">{px(s.ce?.ask)}</td>
                  <td className="px-1.5 text-right">{px(s.pe?.ltp)}</td><td className="px-1.5 text-right">{px(s.pe?.ask)}</td>
                  <td className="px-1.5 text-left text-[#B45309]">{(s.warnings || []).join(", ")}</td></tr>))}</tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

export default function OptionsHistory() {
  const [f, setF] = useState({ mode: "", status: "", stale: "", underlying: "" });
  const [open, setOpen] = useState(null);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const h = usePoll("/history", { ms: 10000, params });
  const rows = h.data?.episodes || [];
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="p-4 space-y-3" data-testid="options-history-page">
      <h1 className="text-lg font-bold flex items-center gap-2"><History className="h-5 w-5" /> Scanner History</h1>
      <div className="flex gap-2 flex-wrap text-xs items-center">
        <select className={sel} value={f.mode} onChange={set("mode")} data-testid="options-history-mode"><option value="">Both modes</option><option>LTP</option><option>OFFER</option></select>
        <select className={sel} value={f.status} onChange={set("status")}><option value="">Open + closed</option><option value="OPEN">Open</option><option value="CLOSED">Closed</option></select>
        <select className={sel} value={f.stale} onChange={set("stale")}><option value="">Any data</option><option value="none">Fresh data only</option><option value="only">Stale LTP only</option></select>
        <input className={sel} placeholder="Underlying" value={f.underlying} onChange={(e) => setF({ ...f, underlying: e.target.value.toUpperCase() })} />
        <span className="text-[var(--term-muted)]">{rows.length} episodes · scanner observations only — not fills, not P&L</span>
      </div>
      {h.error && <div className="text-xs text-[#FF3B30]">{h.error}</div>}
      <div className="term-panel rounded-sm overflow-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
        <table className="w-full text-xs border-collapse" data-testid="options-history-table">
          <thead className="sticky top-0 bg-[var(--term-s3)]"><tr>
            {["Mode", "Underlying", "Expiry", "CE", "PE", "First seen", "Duration", "Obs", "Opp first", "Opp max", "Opp/lot at max", "% on capital at max", "Data", "Status / close reason"].map((x) =>
              <th key={x} className="px-1.5 py-2 text-right whitespace-nowrap">{x}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={14} className="px-4 py-8 text-center text-[var(--term-muted)]">No episodes yet. An episode starts when a pair qualifies in a mode while the scanner runs.</td></tr>}
            {rows.map((e) => (
              <tr key={e.id} className="cursor-pointer hover:bg-[var(--term-hover)] mono" onClick={() => setOpen(e.id)} data-testid={`options-episode-${e.id}`}>
                <td className="px-1.5 py-1 text-right font-bold">{e.mode}</td><td className="px-1.5 text-right">{e.underlying}</td><td className="px-1.5 text-right">{e.expiry}</td>
                <td className="px-1.5 text-right">{e.ce_strike}</td><td className="px-1.5 text-right">{e.pe_strike}</td>
                <td className="px-1.5 text-right whitespace-nowrap">{istTime(e.first_seen_at)}</td><td className="px-1.5 text-right">{age(e.duration_s)}</td>
                <td className="px-1.5 text-right">{e.n}</td><td className="px-1.5 text-right">{px(e.opp_first)}</td>
                <td className="px-1.5 text-right font-bold text-[#00C805]">{px(e.opp_max)}</td><td className="px-1.5 text-right">{px(e.at_max?.opp_lot)}</td>
                <td className="px-1.5 text-right">{e.at_max?.pct_on_capital != null ? `${e.at_max.pct_on_capital.toFixed(2)}%` : "—"}</td>
                <td className={`px-1.5 text-right ${e.stale_observations ? "text-[#B45309]" : ""}`}>{e.stale_observations ? `STALE ${e.stale_observations}/${e.n}` : "fresh"}</td>
                <td className="px-1.5 text-right">{e.status === "OPEN" ? "OPEN" : e.close_reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Episode id={open} onClose={() => setOpen(null)} />
    </div>
  );
}
